import { formatElplanAlertMessage } from 'api/elplan/elplan.alert.helper'
import { resolveElplanErrorAlert } from 'api/elplan/elplan.errorAlert.helper'
import type { BotStrategyConfiguration } from 'api/elplan/elplan.types'
import { BOT_STRATEGY_SETTING_QUERY_KEY, useExportBotStrategySettingMutation } from 'api/elplan/intradayTrading.api'
import { queryClient } from 'helpers/queryClient'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { normalizeDecimal } from 'utils/numberUtils/decimalInput'
import { getBidTimestamp } from '../helpers/BidInputTable.time'
import { makeStrategyCellKey } from '../tableCellAdapters/strategyTableAdapter'

function buildBotStrategyExportSeries(
  config: BotStrategyConfiguration,
  valuesSnapshot: Record<string, string>,
  bidBaseStartTime: Date,
  rowCount: number
) {
  const rowIndices = Array.from({ length: rowCount }, (_, i) => i)
  return config.series.map((s) => ({
    series_id: s.id,
    values: rowIndices.flatMap((i) => {
      const reading = normalizeDecimal(valuesSnapshot[makeStrategyCellKey(i, s.id)] ?? '')
      return /^-?\d+$/.test(reading)
        ? [{ time: getBidTimestamp(bidBaseStartTime, i), value: parseInt(reading, 10) }]
        : []
    }),
  }))
}

export function useBotStrategyExportBehavior(
  systemId: number | undefined,
  botStrategyConfig: BotStrategyConfiguration | undefined,
  bidBaseStartTime: Date,
  rowCount: number
) {
  const { t } = useTranslation()
  const { mutate: exportBotStrategySetting, isPending: isExporting } = useExportBotStrategySettingMutation()
  const botStrategyValuesRef = useRef<Record<string, string>>({})

  const handleBotStrategyValuesChange = (values: Record<string, string>) => {
    botStrategyValuesRef.current = values
  }

  const handleExportBotStrategySetting = () => {
    if (!systemId || !botStrategyConfig) return
    const series = buildBotStrategyExportSeries(
      botStrategyConfig,
      botStrategyValuesRef.current,
      bidBaseStartTime,
      rowCount
    )
    exportBotStrategySetting(
      { system: systemId, series },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: [BOT_STRATEGY_SETTING_QUERY_KEY] })
          alertStore.success(t('Bot strategy setting exported successfully.'))
        },
        onError: (error) => {
          const alert = resolveElplanErrorAlert(error, {
            operation: undefined,
            fallback: { key: 'Failed to export bot strategy setting. Please try again or contact support.' },
          })
          alertStore.error(formatElplanAlertMessage(alert, t), undefined, alert.alertKey)
        },
      }
    )
  }

  return { isExporting, handleBotStrategyValuesChange, handleExportBotStrategySetting }
}
